import React from 'react';
import {
  Card,
  CardHeader,
  TextField,
  Link,
  IconButton,
  Box,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { PageableTable } from 'components';
import { PageableTableHeader } from 'components/PageableTable/components';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { PagedQuoteHistory } from 'lib/Model/QuoteGenerator';
import { TableSettings } from 'hooks';

const useStyles = makeStyles((theme) => ({
  searchBox: {
    padding: theme.spacing(1),
  },
}));

interface QuoteHistoryTableProps {
  quoteHistory?: PagedQuoteHistory;
  loadingHistory: boolean;
  historyTextSearch?: string;
  setHistoryTextSearch: (search: string) => void;
  historyPageNumber: number;
  setHistoryPageNumber: (page: number) => void;
  tableSettings: TableSettings;
  setTableSettings: React.Dispatch<React.SetStateAction<TableSettings>>;
  onViewDetails: (quoteId: number, companyId: string | null) => void;
  onAssociateCompany: (quoteId: number, companyName: string) => void;
}

export const QuoteHistoryTable: React.FC<QuoteHistoryTableProps> = ({
  quoteHistory,
  loadingHistory,
  historyTextSearch,
  setHistoryTextSearch,
  historyPageNumber,
  setHistoryPageNumber,
  tableSettings,
  setTableSettings,
  onViewDetails,
  onAssociateCompany,
}) => {
  const classes = useStyles();

  const historyColumns: PageableTableHeader[] = [
    {
      key: 'date',
      label: 'Created On',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'company_name',
      label: 'Company Name',
      sortable: false,
      props: { align: 'center' },
    },

    {
      key: 'company_size',
      label: 'Company Size',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'deal_stage',
      label: 'Deal Stage',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'items',
      label: 'Items',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'created_by',
      label: 'Created By',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'details',
      label: 'Details',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'Link',
      label: 'Link',
      sortable: false,
      props: { align: 'center' },
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Proposal History"
        subheader="A history of all proposals generated through the system."
      />
      <Box>
        <TextField
          variant="outlined"
          placeholder="Type here to search for a company."
          value={historyTextSearch}
          className={classes.searchBox}
          onChange={(e) => setHistoryTextSearch(e.target.value ?? '')}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      <PageableTable
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        showSkeletonLoading
        hideColumns={false}
        rows={
          quoteHistory?.data?.map((quote, index) => ({
            key: index.toString(),
            cells: [
              {
                key: 'date',
                display: quote.created_on && (
                  <DateDisplay date={quote.created_on} />
                ),
                props: { align: 'center' },
              },
              {
                key: 'company_name',
                display: quote.company_id ? (
                  <Link
                    href={`${window.location.origin}/clients/${quote.company_id}#productstack`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    {quote.company_name}
                  </Link>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    {quote.company_name}
                    <Tooltip title="This proposal is not properly linked with a company. Link them to make the proposal visible on Product Stack.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onAssociateCompany(quote.quote_id, quote.company_name)
                        }
                      >
                        <LinkOffIcon color="error" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
                props: { align: 'center' },
              },
              {
                key: 'company_size',
                display: quote.company_size,
                props: { align: 'center' },
              },
              {
                key: 'deal_stage',
                display: quote.deal_stage || '-',
                props: { align: 'center' },
              },
              {
                key: 'items',
                display: quote.items
                  .split(',')
                  .map((item) =>
                    item
                      .trim()
                      .replace(/_/g, ' ')
                      .replace(/\b(\w)/g, (m) => m.toUpperCase())
                      .replace(/\bHr\b/g, 'HR')
                      .replace(/\bCsp\b/g, 'CSP')
                      .replace(/\bRfid\b/g, 'RFID')
                      .replace(/\bEm\b/g, 'EM'),
                  )
                  .join(', '),
                props: { align: 'center' },
              },
              {
                key: 'created_by',
                display: quote.created_by,
                props: { align: 'center' },
              },
              {
                key: 'details',
                display: (
                  <IconButton
                    onClick={() =>
                      onViewDetails(quote.quote_id, quote.company_id)
                    }
                    size="small"
                    title="View Proposal Details"
                  >
                    <VisibilityIcon />
                  </IconButton>
                ),
                props: { align: 'center' },
              },
              {
                key: 'Link',
                display: quote.quote_slide_id ? (
                  <Link
                    href={`https://docs.google.com/presentation/d/${quote.quote_slide_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    View
                  </Link>
                ) : (
                  '-'
                ),
                props: { align: 'center' },
              },
            ],
          })) ?? []
        }
        loading={loadingHistory}
        pageNumber={historyPageNumber}
        rowCount={quoteHistory?.total_count ?? 0}
        columns={historyColumns}
        onChangePage={setHistoryPageNumber}
      />
    </Card>
  );
};

export default QuoteHistoryTable;
